import type { AgendaItem } from "./schemas";
import { APP_LOCALE } from "utils";

/**
 * The schedule the attendee and admin agenda tabs both render.
 *
 * WHICH CLOCK. Every time and every day here is on the event's clock, the
 * IANA zone stored on `events/{id}.timezone`. Attendees read it from the
 * public projection, organisers from the event itself. A session at 09:00 in
 * Berlin reads 09:00 to everybody, including somebody planning the trip from
 * San Francisco, and the page says which clock that is (`scheduleClockLabel`).
 * A viewer whose own clock differs gets one extra line saying by how much
 * (`viewerClockHint`), rather than a second time on every card.
 *
 * The admin date and time fields use the same clock (`resolveWallTime`,
 * `instantToWallTime`), so what an organiser types is what the list shows.
 *
 * A zone that is missing or that this browser cannot resolve falls back to
 * the viewer's own clock, and the line then says so (`usableTimeZone`). That
 * covers a projection written before it carried `timezone`.
 *
 * WHY NOT `dayKey`. The trigger computes `dayKey` in the event's timezone,
 * which is the same day `dayKeyOf(start, zone)` gives here. Grouping by the
 * start time computed on the client lets a session appear under its day the
 * moment it is written, before `reconcile_agenda_day` has run, and keeps the
 * fallback clock consistent with the times it prints.
 */

export type ScheduleDay = {
  /** YYYY-MM-DD on the schedule's clock. Stable, so it is safe in test ids. */
  key: string;
  items: AgendaItem[];
};

export type Schedule = {
  days: ScheduleDay[];
  /** Items with no readable start time, which no day can hold. */
  unscheduled: AgendaItem[];
};

/**
 * One `Intl.DateTimeFormat` per locale, zone and option set, built once.
 *
 * Constructing a formatter costs far more than calling one, and a 150-session
 * schedule used to build several per card on every render.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = JSON.stringify([locale ?? null, options]);
  let cached = formatters.get(key);
  if (!cached) {
    cached = new Intl.DateTimeFormat(locale ?? APP_LOCALE, options);
    formatters.set(key, cached);
  }
  return cached;
}

/** A Firestore Timestamp, a Date or an ISO string, as a Date. */
export function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "object" && "toDate" in value) {
    try {
      const date = (value as { toDate: () => Date }).toDate();
      return Number.isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

/** YYYY-MM-DD of `date` on `timeZone` (the viewer's clock when omitted). */
export function dayKeyOf(date: Date, timeZone?: string): string {
  // en-CA formats a date as YYYY-MM-DD, which is the key shape wanted.
  return formatter("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Group by day in time order, and set aside what has no time.
 *
 * Within a day: start time, then a highlighted session before one that is
 * not (only when they start together), then end time (shorter first), then
 * title, so two parallel sessions always come out in the same order however
 * the snapshot arrived. Unscheduled items sort by title, since there is nothing else.
 */
export function groupAgendaByDay(
  items: AgendaItem[],
  timeZone?: string,
): Schedule {
  const byDay = new Map<string, { item: AgendaItem; start: number }[]>();
  const unscheduled: AgendaItem[] = [];

  for (const item of items) {
    const start = toDate(item.startsAt);
    if (!start) {
      unscheduled.push(item);
      continue;
    }
    const key = dayKeyOf(start, timeZone);
    const list = byDay.get(key);
    const entry = { item, start: start.getTime() };
    if (list) list.push(entry);
    else byDay.set(key, [entry]);
  }

  const days = [...byDay.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entries]) => ({
      key,
      items: entries
        .sort(
          (a, b) =>
            a.start - b.start ||
            highlightRank(a.item) - highlightRank(b.item) ||
            // Two highlights of one start keep the order their chapter numbers
            // use (start, then id), so a card never reads 03 before 01.
            (a.item.highlight === true && b.item.highlight === true
              ? a.item.id.localeCompare(b.item.id)
              : 0) ||
            endMillis(a.item) - endMillis(b.item) ||
            a.item.title.localeCompare(b.item.title),
        )
        .map(({ item }) => item),
    }));

  return {
    days,
    unscheduled: [...unscheduled].sort((a, b) =>
      a.title.localeCompare(b.title),
    ),
  };
}

/** 0 for a highlighted session, 1 for any other: lower sorts first. */
function highlightRank(item: AgendaItem): number {
  return item.highlight === true ? 0 : 1;
}

function endMillis(item: AgendaItem): number {
  return toDate(item.endsAt)?.getTime() ?? Number.POSITIVE_INFINITY;
}

/** Noon on the key's date, so no zone offset can push it onto a neighbour. */
function dayKeyToUtcNoon(key: string): Date {
  return new Date(`${key}T12:00:00Z`);
}

/** "Thursday, 10 September" for the section header. */
export function formatDayHeading(key: string, locale?: string): string {
  return formatter(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(dayKeyToUtcNoon(key));
}

/** "Thu 10 Sep" for the day picker, where a phone has little width. */
export function formatDayChip(key: string, locale?: string): string {
  return formatter(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(dayKeyToUtcNoon(key));
}

/** "09:00" on the viewer's clock, or on `timeZone` when given. */
export function formatClock(
  value: unknown,
  timeZone?: string,
  locale?: string,
): string {
  const date = toDate(value);
  if (!date) return "";
  return formatter(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(date);
}

/**
 * The end time, and how many days after the start's day it falls.
 *
 * A session running past midnight would otherwise read as "22:00 – 01:00"
 * under one day, which looks like a typo for 01:00 the same morning. The day
 * count is kept apart from the clock so the card can print it as a short
 * "+1 day" line, and every time column stays the width of one time.
 */
export function formatEnd(
  item: AgendaItem,
  timeZone?: string,
  locale?: string,
): { clock: string; daysLater: number } | null {
  const start = toDate(item.startsAt);
  const end = toDate(item.endsAt);
  if (!start || !end) return null;
  const daysLater = Math.round(
    (dayKeyToUtcNoon(dayKeyOf(end, timeZone)).getTime() -
      dayKeyToUtcNoon(dayKeyOf(start, timeZone)).getTime()) /
      86_400_000,
  );
  return { clock: formatClock(end, timeZone, locale), daysLater };
}

/** Room, then location: the two place fields an organiser can fill in. */
export function placeLine(item: AgendaItem): string {
  return [item.roomName, item.location]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" · ");
}

/** The viewer's IANA zone, e.g. "Europe/Berlin". */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The name of a clock at a moment, e.g. "Central European Summer Time".
 *
 * Named at a moment because the name changes with daylight saving.
 */
export function clockLabel(
  at: Date,
  timeZone?: string,
  locale?: string,
): string {
  return zoneName(at, "long", timeZone, locale);
}

/**
 * The name of the clock a schedule is printed on.
 *
 * The name in force at the earliest session, so the line does not depend on
 * the order the sessions arrived in. When the sessions fall on both sides of
 * a daylight-saving change, one seasonal name is wrong for some of them
 * ("Central European Summer Time" over a session in late October), so the
 * zone's all-year name ("Central European Time") is used instead.
 */
export function scheduleClockLabel(
  moments: Date[],
  timeZone?: string,
  locale?: string,
): string | null {
  if (moments.length === 0) return null;
  let earliest = moments[0];
  const names = new Set<string>();
  for (const at of moments) {
    if (at.getTime() < earliest.getTime()) earliest = at;
    names.add(clockLabel(at, timeZone, locale));
  }
  return names.size > 1
    ? zoneName(earliest, "longGeneric", timeZone, locale)
    : clockLabel(earliest, timeZone, locale);
}

function zoneName(
  at: Date,
  style: "long" | "longGeneric",
  timeZone?: string,
  locale?: string,
): string {
  const part = formatter(locale, { timeZone, timeZoneName: style })
    .formatToParts(at)
    .find((p) => p.type === "timeZoneName");
  return part?.value ?? timeZone ?? viewerTimeZone();
}

/** UTC offset of `timeZone` at `at`, in minutes. */
export function offsetMinutes(at: Date, timeZone: string): number {
  const parts = formatter("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(at);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
  );
  return Math.round((asUtc - Math.floor(at.getTime() / 60000) * 60000) / 60000);
}

/**
 * The zone to print a schedule on: `timeZone` when this browser can resolve
 * it, otherwise undefined, which every formatter here reads as the viewer's
 * own clock.
 */
export function usableTimeZone(
  timeZone: string | null | undefined,
): string | undefined {
  if (!timeZone) return undefined;
  try {
    formatter("en-US", { timeZone });
    return timeZone;
  } catch {
    return undefined;
  }
}

const WALL_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * What a wall time on `timeZone`'s clock names.
 *
 * - `exact`: one instant, the usual case.
 * - `repeated`: the clocks go back and pass this time twice. `instant` is the
 *   first pass, still on the earlier offset, which is what `zoneinfo` does
 *   with `fold=0` too.
 * - `skipped`: the clocks go forward past this time, so it names no instant.
 * - `invalid`: not a wall time at all, or a date like 2026-02-30.
 */
export type WallTimeResolution =
  | { kind: "exact" | "repeated"; instant: Date }
  | { kind: "skipped" | "invalid" };

/**
 * Resolve a "YYYY-MM-DDTHH:mm" wall time on `timeZone`'s clock.
 *
 * Built from offsets rather than `new Date(string)`, which reads the string on
 * the browser's clock, so an organiser in Los Angeles entering a Berlin
 * session gets Berlin's 09:00.
 *
 * The instant is the wall time read as UTC, less the zone's offset, and every
 * offset in use is between UTC-12 and UTC+14. So the instant lies in the 26
 * hours from 14 hours before that UTC reading to 12 hours after it, and the
 * offsets just outside that window, at -15h and +13h, are the ones in force
 * before and after any change inside it. No zone changes its clock twice in
 * so short a span. Each candidate must read back as the wall time asked for,
 * which is also what rejects a "2026-02-30" that Date.UTC would roll into
 * March.
 */
export function resolveWallTime(
  wall: string,
  timeZone: string | undefined,
): WallTimeResolution {
  const match = WALL_TIME.exec(wall);
  if (!match) return { kind: "invalid" };
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  if (Number.isNaN(asUtc) || hour > 23 || minute > 59)
    return { kind: "invalid" };
  const zone = timeZone ?? viewerTimeZone();
  const before = offsetMinutes(new Date(asUtc - 15 * 3_600_000), zone);
  const after = offsetMinutes(new Date(asUtc + 13 * 3_600_000), zone);
  const candidates = [...new Set([before, after])]
    .map((offset) => asUtc - offset * 60_000)
    .filter((instant) => instantToWallTime(new Date(instant), zone) === wall)
    .sort((a, b) => a - b);
  if (candidates.length === 0) {
    // Only a change that moves the clocks forward leaves a gap. A real date
    // that reads back as nothing is in one; a rolled-over date is not.
    const rolled = new Date(asUtc).getUTCDate() !== day;
    return { kind: rolled || after <= before ? "invalid" : "skipped" };
  }
  return {
    kind: candidates.length > 1 ? "repeated" : "exact",
    instant: new Date(candidates[0]),
  };
}

/**
 * "2026-03-29T09:00" on `timeZone`'s clock as the instant it names, or null
 * when it names none. A repeated time gives its first pass; see
 * `resolveWallTime`.
 */
export function wallTimeToInstant(
  wall: string,
  timeZone: string | undefined,
): Date | null {
  const resolved = resolveWallTime(wall, timeZone);
  return "instant" in resolved ? resolved.instant : null;
}

/** An instant as "YYYY-MM-DDTHH:mm" on `timeZone`'s clock, or "". */
export function instantToWallTime(
  value: unknown,
  timeZone: string | undefined,
): string {
  const date = toDate(value);
  if (!date) return "";
  const parts = formatter("en-US", {
    timeZone: timeZone ?? viewerTimeZone(),
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get(
    "minute",
  )}`;
}

/**
 * One line for a viewer whose clock is not the event's, or null.
 *
 * "Your device is 9 hours behind Berlin time." The difference is taken at
 * every session start, because a daylight-saving change on one side only
 * moves it: Berlin and New York are six hours apart most of the year and five
 * for a few weeks in spring and autumn. When it varies across the schedule
 * the line gives no number rather than a wrong one.
 */
export function viewerClockHint(
  moments: Date[],
  eventZone: string | undefined,
  place: string,
  viewerZone: string = viewerTimeZone(),
): string | null {
  if (!eventZone || moments.length === 0) return null;
  let differences: Set<number>;
  try {
    differences = new Set(
      moments.map(
        (at) => offsetMinutes(at, viewerZone) - offsetMinutes(at, eventZone),
      ),
    );
  } catch {
    return null;
  }
  if (differences.size === 1 && differences.has(0)) return null;
  if (differences.size > 1)
    return `Your device is on a different clock from ${place} time, and the gap changes during the event.`;
  const [minutes] = [...differences];
  const direction = minutes > 0 ? "ahead of" : "behind";
  return `Your device is ${describeDuration(Math.abs(minutes))} ${direction} ${place} time.`;
}

function describeDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const parts = [
    hours > 0 ? `${hours} ${hours === 1 ? "hour" : "hours"}` : "",
    rest > 0 ? `${rest} ${rest === 1 ? "minute" : "minutes"}` : "",
  ].filter(Boolean);
  return parts.join(" ");
}
